/**
 * Date range presets — frontend mirror of the backend resolver
 * (mcl/utils/date_range_presets.py).
 *
 * Presets are stored as codes (e.g. "this_week") and resolved into concrete
 * `_min`/`_max` dates by the backend FilterSet at request time, so a stored
 * preset always matches the *current* week/month relative to "today".
 *
 * The frontend only needs to resolve a code for UI purposes (calendar
 * preview + label). The stored value is always the code.
 */

export const DATE_RANGE_PRESETS = [
    "this_week",
    "last_week",
    "this_month",
    "last_month",
    "this_quarter",
    "this_year",
    "last_30_days",
];

export const ALLOWED_DATE_RANGE_PRESETS = new Set(DATE_RANGE_PRESETS);

function toISO(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

function startOfWeek(today) {
    // JS getDay(): Sunday = 0 ... Saturday = 6. Week starts on Monday.
    const offset = (today.getDay() + 6) % 7;
    const start = new Date(today);
    start.setDate(today.getDate() - offset);
    start.setHours(0, 0, 0, 0);
    return start;
}

function endOfWeek(today) {
    const start = startOfWeek(today);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return end;
}

function monthBounds(year, month) {
    // month is 0-based here.
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return [start, end];
}

/**
 * Resolve a preset code into an inclusive { min, max } pair of ISO date strings,
 * relative to `today` (defaults to now, in local time).
 *
 * @param {string} code
 * @param {Date} [today]
 * @returns {{min: string, max: string}|null}
 */
export function resolveDateRangePreset(code, today = new Date()) {
    if (!ALLOWED_DATE_RANGE_PRESETS.has(code)) {
        return null;
    }
    const ref = new Date(today);
    ref.setHours(0, 0, 0, 0);

    let start;
    let end;

    switch (code) {
        case "this_week":
            start = startOfWeek(ref);
            end = endOfWeek(ref);
            break;
        case "last_week": {
            const thisStart = startOfWeek(ref);
            start = new Date(thisStart);
            start.setDate(thisStart.getDate() - 7);
            end = new Date(start);
            end.setDate(start.getDate() + 6);
            break;
        }
        case "this_month":
            [start, end] = monthBounds(ref.getFullYear(), ref.getMonth());
            break;
        case "last_month": {
            const year = ref.getMonth() === 0 ? ref.getFullYear() - 1 : ref.getFullYear();
            const month = ref.getMonth() === 0 ? 11 : ref.getMonth() - 1;
            [start, end] = monthBounds(year, month);
            break;
        }
        case "this_quarter": {
            const quarterStartMonth = Math.floor(ref.getMonth() / 3) * 3;
            [start, end] = monthBounds(ref.getFullYear(), quarterStartMonth + 2);
            break;
        }
        case "this_year":
            start = new Date(ref.getFullYear(), 0, 1);
            end = new Date(ref.getFullYear(), 11, 31);
            break;
        case "last_30_days":
            start = new Date(ref);
            start.setDate(ref.getDate() - 30);
            end = new Date(ref);
            break;
        default:
            return null;
    }

    return { min: toISO(start), max: toISO(end) };
}

export const DATE_RANGE_PRESET_LABEL_KEYS = {
    this_week: "dateRangePreset.thisWeek",
    last_week: "dateRangePreset.lastWeek",
    this_month: "dateRangePreset.thisMonth",
    last_month: "dateRangePreset.lastMonth",
    this_quarter: "dateRangePreset.thisQuarter",
    this_year: "dateRangePreset.thisYear",
    last_30_days: "dateRangePreset.last30Days",
};

export function dateRangePresetLabel(vm, code) {
    const key = DATE_RANGE_PRESET_LABEL_KEYS[code];
    return key ? vm.$translate(key) : code;
}

/**
 * Read the active preset code for a field from a filters object, if any.
 * @returns {string|null}
 */
export function activeDateRangePreset(filters, field) {
    const value = filters?.[`${field}_preset`];
    return ALLOWED_DATE_RANGE_PRESETS.has(value) ? value : null;
}
